/**
 * Initiative Portfolio route tree, mounted by the shell at /portfolio.
 *
 * Role-gated (2026-08-08): ProtectedRoute requires a Portfolio.* role
 * (Portfolio.User for readers; global Admin passes). Edit rights are
 * per-initiative (accountable owner / delivery lead) plus Portfolio.Admin;
 * sensitive rows additionally need portfolio.view-sensitive. Pages read
 * `me.canAdmin` / `me.canViewSensitive` from /api/portfolio/meta.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import {
  CalendarLtrRegular,
  ClipboardTaskListLtrRegular,
  DataPieRegular,
  MailRegular,
  PeopleTeamRegular,
  PersonNoteRegular,
  ScalesRegular,
  SettingsRegular,
  ShieldErrorRegular,
  TableRegular,
  TaskListSquareLtrRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { AppShell, type NavEntry } from '@widgets/app-shell';
const ModuleGuidePage = lazy(() =>
  import('@widgets/module-guide').then((m) => ({ default: m.ModuleGuidePage }))
);
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import { usePortfolioMeta } from '@portfolio/api/usePortfolio';

/** Twilight Plum: the module's locked accent (brand secondary). */
export const PORTFOLIO_ACCENT = '#553746';

const PortfolioListPage = lazy(() =>
  import('@portfolio/pages/portfolio').then((m) => ({ default: m.PortfolioListPage }))
);
const InitiativeDetailPage = lazy(() =>
  import('@portfolio/pages/initiative').then((m) => ({ default: m.InitiativeDetailPage }))
);
const InitiativePrintPage = lazy(() =>
  import('@portfolio/pages/initiative').then((m) => ({ default: m.InitiativePrintPage }))
);
const UpdateFlowPage = lazy(() =>
  import('@portfolio/pages/update').then((m) => ({ default: m.UpdateFlowPage }))
);
const PortfolioDashboardPage = lazy(() =>
  import('@portfolio/pages/dashboard').then((m) => ({ default: m.PortfolioDashboardPage }))
);
const BlockersPage = lazy(() =>
  import('@portfolio/pages/blockers').then((m) => ({ default: m.BlockersPage }))
);
const RisksPage = lazy(() =>
  import('@portfolio/pages/risks').then((m) => ({ default: m.RisksPage }))
);
const DecisionsPage = lazy(() =>
  import('@portfolio/pages/decisions').then((m) => ({ default: m.DecisionsPage }))
);
const MyWorkPage = lazy(() =>
  import('@portfolio/pages/my-work').then((m) => ({ default: m.MyWorkPage }))
);
const PortfolioSettingsPage = lazy(() =>
  import('@portfolio/pages/settings').then((m) => ({ default: m.PortfolioSettingsPage }))
);
const EmailDigestPage = lazy(() =>
  import('@portfolio/pages/digest').then((m) => ({ default: m.EmailDigestPage }))
);
const VeltBoardPage = lazy(() =>
  import('@portfolio/pages/velt').then((m) => ({ default: m.VeltBoardPage }))
);
const VeltMeetingPage = lazy(() =>
  import('@portfolio/pages/velt').then((m) => ({ default: m.VeltMeetingPage }))
);
const VeltMeetingsPage = lazy(() =>
  import('@portfolio/pages/velt').then((m) => ({ default: m.VeltMeetingsPage }))
);
const VeltMeetingPrintPage = lazy(() =>
  import('@portfolio/pages/velt').then((m) => ({ default: m.VeltMeetingPrintPage }))
);
const VeltReminderPage = lazy(() =>
  import('@portfolio/pages/velt').then((m) => ({ default: m.VeltReminderPage }))
);
const VeltActionsPage = lazy(() =>
  import('@portfolio/pages/velt').then((m) => ({ default: m.VeltActionsPage }))
);

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>
      {children}
    </Suspense>
  );
}

const PortfolioBasePathContext = createContext<string>('');

/** Base path the module is mounted at ('/portfolio' in the shell). */
export function usePortfolioBasePath(): string {
  return useContext(PortfolioBasePathContext);
}

function buildNavItems(
  basePath: string,
  canAdmin: boolean,
  canVelt: boolean,
  hasDeliveryWork: boolean
): NavEntry[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [
    { path: p('/'), label: 'Portfolio', icon: TableRegular },
    { path: p('/dashboard'), label: 'Dashboard', icon: DataPieRegular },
    // Under the dashboard, which summarises blockers, and above the personal
    // pages: these are whole-register reads, not worklists.
    { path: p('/blockers'), label: 'Blockers', icon: WarningRegular },
    // Risks sits beside Blockers rather than in the Delivery section below
    // (ADO #1551): what might go wrong across the company is a leadership
    // question, and the page opens on high exposure only so a leader sees the
    // short list. The full working register is one switch away for the people
    // who maintain it.
    { path: p('/risks'), label: 'Risks', icon: ShieldErrorRegular },
    // The last of the four RAID surfaces, and a main-nav item for the same
    // reason as Risks (ADO #1495): "why did we choose X" is an executive
    // question. It sits after Risks so the three read left to right as what
    // is stuck, what might go wrong, and what we decided about it.
    { path: p('/decisions'), label: 'Decisions', icon: ScalesRegular },
    { path: p('/my-updates'), label: 'My Updates', icon: ClipboardTaskListLtrRegular },
    { path: p('/email-digest'), label: 'Email Digest', icon: MailRegular },
    // My Work is its own SECTION rather than another top-level item, for the
    // same reason VELT is: it is a different mode of use. It appears for
    // people who actually have delivery work (named on an initiative, or
    // owning an open risk or next step), which is deliberately DATA-driven
    // rather than a new role: nothing extra to grant in Vera Admin, and a
    // pure leadership reader never sees the delivery surfaces at all.
    ...(hasDeliveryWork
      ? ([
          { kind: 'section', label: 'Delivery' },
          { path: p('/my-work'), label: 'My Work', icon: PersonNoteRegular },
        ] as NavEntry[])
      : []),
    // The leadership review is its own SECTION, not one item: it is a
    // different mode of use (a weekly meeting, not the daily register), and
    // the whole group appears only for portfolio.velt holders. The BFF gates
    // every route behind it regardless of what the nav renders.
    ...(canVelt
      ? ([
          { kind: 'section', label: 'VELT' },
          { path: p('/velt'), label: 'VELT Portfolio', icon: PeopleTeamRegular },
          { path: p('/velt/meetings'), label: 'Meetings', icon: CalendarLtrRegular },
          // The open-items list (Sasha, 2026-08-31): what the room promised,
          // grouped by the call that promised it. Its own nav item because it
          // is what leadership opens BETWEEN meetings.
          { path: p('/velt/actions'), label: 'Next Steps', icon: TaskListSquareLtrRegular },
        ] as NavEntry[])
      : []),
    ...(canAdmin
      ? ([
          { kind: 'section', label: 'Admin' },
          { path: p('/settings'), label: 'Settings', icon: SettingsRegular },
        ] as NavEntry[])
      : []),
  ];
}

export function PortfolioRootLayout() {
  const basePath = usePortfolioBasePath();
  // Admin-only nav (Settings). The query 403s harmlessly for users without a
  // Portfolio role; ProtectedRoute below blocks them anyway.
  const me = usePortfolioMeta().data?.me;
  const canAdmin = me?.canAdmin ?? false;
  const canVelt = me?.canVelt ?? false;
  const hasDeliveryWork = me?.hasDeliveryWork ?? false;
  return (
    <ProtectedRoute appRolePrefix="Portfolio">
      <ErrorBoundary>
        <AppShell
          appId="portfolio"
          appTitle="Vera Portfolio"
          headerColor={PORTFOLIO_ACCENT}
          navItems={buildNavItems(basePath, canAdmin, canVelt, hasDeliveryWork)}
        >
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

export function portfolioRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <PortfolioBasePathContext.Provider value={basePath}>
          <PortfolioRootLayout />
        </PortfolioBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><PortfolioListPage /></LazyPage> },
        { path: 'dashboard', element: <LazyPage><PortfolioDashboardPage /></LazyPage> },
        { path: 'blockers', element: <LazyPage><BlockersPage /></LazyPage> },
        { path: 'risks', element: <LazyPage><RisksPage /></LazyPage> },
        { path: 'decisions', element: <LazyPage><DecisionsPage /></LazyPage> },
        // Routable for everyone with module access even when the nav item is
        // hidden: the page renders its own empty state, and a bookmarked URL
        // that 404s because a next step was closed would be worse than an
        // honest "nothing is waiting on you".
        { path: 'my-work', element: <LazyPage><MyWorkPage /></LazyPage> },
        { path: 'my-updates', element: <LazyPage><UpdateFlowPage /></LazyPage> },
        { path: 'email-digest', element: <LazyPage><EmailDigestPage /></LazyPage> },
        // VELT: role-gated in the BFF (portfolio.velt) rather than by a second
        // ProtectedRoute, so a user without it gets a clean 403 from the API
        // instead of the shell's blanket access screen.
        { path: 'velt', element: <LazyPage><VeltBoardPage /></LazyPage> },
        { path: 'velt/meetings', element: <LazyPage><VeltMeetingsPage /></LazyPage> },
        { path: 'velt/meetings/:id', element: <LazyPage><VeltMeetingPage /></LazyPage> },
        { path: 'velt/meetings/:id/print', element: <LazyPage><VeltMeetingPrintPage /></LazyPage> },
        { path: 'velt/reminders', element: <LazyPage><VeltReminderPage /></LazyPage> },
        { path: 'velt/actions', element: <LazyPage><VeltActionsPage /></LazyPage> },
        { path: 'settings', element: <LazyPage><PortfolioSettingsPage /></LazyPage> },
        { path: 'initiatives/:id', element: <LazyPage><InitiativeDetailPage /></LazyPage> },
        // Its own route rather than a print stylesheet over the detail page:
        // paper wants every card opened out in a fixed order, which would
        // compromise the screen layout people use daily. Same call the VELT
        // agenda made.
        { path: 'initiatives/:id/print', element: <LazyPage><InitiativePrintPage /></LazyPage> },
        // The module's whole documentation set, read here rather than in Academy
        // (Sasha, 2026-08-16). Content is authored in Academy and never copied.
        { path: 'guide', element: <LazyPage><ModuleGuidePage module="portfolio" basePath={basePath} /></LazyPage> },
        { path: 'guide/:slug', element: <LazyPage><ModuleGuidePage module="portfolio" basePath={basePath} /></LazyPage> },
        { path: '*', element: <Navigate to={basePath || '/'} replace /> },
      ],
    },
  ];
}
